import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Mail,
  RefreshCw,
  Trash2,
  AlertCircle,
  Check,
  Loader2,
  Users,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  useConnectedAccounts,
  useConnectEmail,
  useDisconnectEmail,
  useSyncEmail,
} from '@/hooks/use-connected-accounts'
import {
  useGoogleContactsAccounts,
  useConnectGoogleContacts,
  useDisconnectGoogleContacts,
  useSyncGoogleContacts,
} from '@/hooks/use-google-contacts'
import type { ConnectedAccount, EmailProvider } from '@/lib/types'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

const PROVIDERS: { id: EmailProvider; name: string; icon: string; color: string }[] = [
  { id: 'gmail', name: 'Gmail', icon: '📧', color: 'bg-coral-soft text-coral' },
  {
    id: 'outlook',
    name: 'Outlook',
    icon: '📨',
    color: 'bg-lilac-soft text-lilac-foreground dark:text-lilac',
  },
]

const ALL_PROVIDER_INFO: Record<string, { name: string; icon: string; color: string }> = {
  gmail: { name: 'Gmail', icon: '📧', color: 'bg-coral-soft text-coral' },
  outlook: {
    name: 'Outlook',
    icon: '📨',
    color: 'bg-lilac-soft text-lilac-foreground dark:text-lilac',
  },
  google_contacts: {
    name: 'Google Contacts',
    icon: '👥',
    color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  },
}

function AccountCard({
  account,
  onDisconnect,
  onSync,
}: {
  account: ConnectedAccount
  onDisconnect: () => void
  onSync: () => void
}) {
  const provider = ALL_PROVIDER_INFO[account.provider]
  const [syncing, setSyncing] = useState(false)

  const handleSync = async () => {
    setSyncing(true)
    onSync()
    setTimeout(() => setSyncing(false), 2000)
  }

  return (
    <div className="bg-surface-container-low rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex size-10 items-center justify-center rounded-full text-lg',
              provider?.color,
            )}
          >
            {provider?.icon}
          </div>
          <div>
            <p className="font-semibold">{account.email}</p>
            <p className="text-on-surface-variant text-sm">{provider?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {account.status === 'active' && (
            <span className="text-chartreuse-foreground dark:text-chartreuse flex items-center gap-1 text-xs">
              <Check className="size-3" /> Connected
            </span>
          )}
          {account.status === 'error' && (
            <span className="text-coral flex items-center gap-1 text-xs">
              <AlertCircle className="size-3" /> Error
            </span>
          )}
          {account.status === 'revoked' && (
            <span className="text-destructive flex items-center gap-1 text-xs">
              <AlertCircle className="size-3" /> Revoked
            </span>
          )}
        </div>
      </div>

      {account.error && <p className="text-destructive mt-2 text-xs">{account.error}</p>}

      {account.lastSyncAt && (
        <p className="text-on-surface-variant mt-2 text-xs">
          Last synced: {new Date(account.lastSyncAt).toLocaleString()}
          {account.syncedContactCount != null && ` · ${account.syncedContactCount} contacts synced`}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-lg text-xs"
          onClick={handleSync}
          disabled={syncing || account.status === 'revoked'}
        >
          {syncing ? (
            <Loader2 className="mr-1 size-3 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 size-3" />
          )}
          Sync now
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="text-destructive border-destructive/30 hover:bg-destructive/10 rounded-lg text-xs"
          onClick={onDisconnect}
        >
          <Trash2 className="mr-1 size-3" />
          Disconnect
        </Button>
      </div>
    </div>
  )
}

export default function ConnectedAccountsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isLoading } = useConnectedAccounts()
  const connectEmail = useConnectEmail()
  const disconnectEmail = useDisconnectEmail()
  const syncEmail = useSyncEmail()

  // Google Contacts
  const { data: gcData, isLoading: gcLoading } = useGoogleContactsAccounts()
  const connectGC = useConnectGoogleContacts()
  const disconnectGC = useDisconnectGoogleContacts()
  const syncGC = useSyncGoogleContacts()

  const accounts = data?.items || []
  const gcAccounts = gcData?.items || []

  const handleConnect = async (provider: EmailProvider) => {
    try {
      await connectEmail.mutateAsync(provider)
      queryClient.invalidateQueries({ queryKey: ['connected-accounts'] })
      toast.success(`${provider === 'gmail' ? 'Gmail' : 'Outlook'} connected successfully`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect')
    }
  }

  const handleDisconnect = async (accountId: string) => {
    try {
      await disconnectEmail.mutateAsync(accountId)
      toast.success('Account disconnected')
    } catch {
      toast.error('Failed to disconnect account')
    }
  }

  const handleSync = async (accountId: string) => {
    try {
      const result = await syncEmail.mutateAsync(accountId)
      toast.success(`Synced ${result.synced} new emails`)
    } catch {
      toast.error('Sync failed')
    }
  }

  const handleConnectGC = async () => {
    try {
      await connectGC.mutateAsync()
      queryClient.invalidateQueries({ queryKey: ['google-contacts-accounts'] })
      toast.success('Google Contacts connected successfully')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect')
    }
  }

  const handleDisconnectGC = async (accountId: string) => {
    try {
      await disconnectGC.mutateAsync(accountId)
      toast.success('Google Contacts disconnected')
    } catch {
      toast.error('Failed to disconnect')
    }
  }

  const handleSyncGC = async (accountId: string) => {
    try {
      const result = await syncGC.mutateAsync(accountId)
      toast.success(
        `Sync complete: ${result.imported} imported, ${result.updated} updated, ${result.pushed} pushed`,
      )
    } catch {
      toast.error('Sync failed')
    }
  }

  return (
    <div className="flex flex-1 flex-col px-6 pt-6 pb-8">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="text-on-surface-variant hover:text-foreground"
        >
          <ArrowLeft className="size-5" />
        </button>
        <h1 className="font-heading text-2xl font-bold">Connected Accounts</h1>
      </div>

      <p className="text-on-surface-variant mb-6 text-sm">
        Connect your accounts to automatically sync contacts and track interactions. Email syncs
        metadata only — never full message bodies.
      </p>

      <div className="space-y-4">
        {/* Google Contacts section */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <h2 className="mb-2 flex items-center gap-2 font-semibold">
            <Users className="text-on-surface-variant size-4" />
            Google Contacts
          </h2>
          <p className="text-on-surface-variant mb-3 text-xs">
            Sync your Google Contacts bidirectionally. New contacts, updates, and deletions are
            synced both ways every 15 minutes.
          </p>
          {gcAccounts.length === 0 && (
            <Button
              variant="outline"
              className="w-full rounded-xl"
              onClick={handleConnectGC}
              disabled={connectGC.isPending}
            >
              {connectGC.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <span className="mr-2">👥</span>
              )}
              Connect Google Contacts
            </Button>
          )}
          {gcLoading && (
            <div className="flex justify-center py-4">
              <Loader2 className="text-primary size-5 animate-spin" />
            </div>
          )}
          {gcAccounts.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              onDisconnect={() => handleDisconnectGC(account.id)}
              onSync={() => handleSyncGC(account.id)}
            />
          ))}
        </section>

        {/* Email connect buttons */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <h2 className="mb-3 flex items-center gap-2 font-semibold">
            <Mail className="text-on-surface-variant size-4" />
            Add Email Account
          </h2>
          <div className="flex gap-2">
            {PROVIDERS.map((provider) => (
              <Button
                key={provider.id}
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => handleConnect(provider.id)}
                disabled={connectEmail.isPending}
              >
                <span className="mr-2">{provider.icon}</span>
                {provider.name}
              </Button>
            ))}
          </div>
        </section>

        {/* Connected email accounts list */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 className="text-primary size-6 animate-spin" />
          </div>
        )}

        {!isLoading && accounts.length === 0 && !gcLoading && gcAccounts.length === 0 && (
          <div className="text-on-surface-variant py-8 text-center text-sm">
            No accounts connected yet.
          </div>
        )}

        {accounts.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            onDisconnect={() => handleDisconnect(account.id)}
            onSync={() => handleSync(account.id)}
          />
        ))}
      </div>
    </div>
  )
}
